// VER-218: Account settings screen (Step 11.6). Shows the caller's own account and
// lets an admin edit the invite domain-allowlist. Saving is step-up-gated
// (PATCH /console/account/allowed-domains), so this screen carries its own OTP modal.
import { useEffect, useState, useCallback } from "react";
import {
  App,
  Card,
  Descriptions,
  Select,
  Button,
  Space,
  Tag,
  Typography,
  Skeleton,
} from "antd";
import { accountApi } from "../../console/consoleApi";
import { useStepUp } from "../../console/useStepUp";
import { errorMessage } from "../../console/consoleUtils";

const { Title, Text } = Typography;

const isCancelled = (err) => err?.message === "STEP_UP_CANCELLED";

export default function ConsoleAccount() {
  const { message } = App.useApp();
  const { withStepUp, modal } = useStepUp();

  const [account, setAccount] = useState(null);
  const [domains, setDomains] = useState([]); // pending selection
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setAccount(null);
    try {
      const res = await accountApi.get();
      const a = res.data.data.account;
      setAccount(a);
      setDomains(a.allowedDomains ?? []);
    } catch (err) {
      message.error(errorMessage(err));
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  // Order-insensitive diff against the saved allowlist.
  const current = account?.allowedDomains ?? [];
  const changed =
    domains.length !== current.length || domains.some((d) => !current.includes(d));

  const save = async () => {
    setSaving(true);
    try {
      const res = await withStepUp((token) => accountApi.setAllowedDomains(domains, token));
      const updated = res.data.data.account;
      setAccount(updated);
      setDomains(updated.allowedDomains ?? []);
      message.success("Allowed domains updated.");
    } catch (err) {
      if (!isCancelled(err)) message.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={
        <Title level={4} style={{ margin: 0 }}>
          Account
        </Title>
      }
    >
      {!account ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <>
          <Descriptions column={1} bordered size="small" style={{ marginBottom: 24 }}>
            <Descriptions.Item label="Name">{account.name}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={account.isActive ? "green" : "red"}>
                {account.isActive ? "active" : "inactive"}
              </Tag>
            </Descriptions.Item>
          </Descriptions>

          <Title level={5} style={{ marginBottom: 4 }}>
            Allowed email domains
          </Title>
          <Text type="secondary">
            Invites are rejected unless the invitee’s email domain is on this list. Leave
            empty to allow any domain.
          </Text>
          <div style={{ marginTop: 12, maxWidth: 520 }}>
            <Select
              mode="tags"
              value={domains}
              onChange={(vals) =>
                setDomains([...new Set(vals.map((v) => v.trim().toLowerCase().replace(/^@/, "")).filter(Boolean))])
              }
              style={{ width: "100%" }}
              placeholder="e.g. company.com"
              tokenSeparators={[",", " "]}
              open={false} // free-entry only; no dropdown suggestions
              disabled={saving}
            />
            <div style={{ marginTop: 12 }}>
              <Space>
                <Button type="primary" onClick={save} loading={saving} disabled={!changed}>
                  Save
                </Button>
                {changed && (
                  <Button onClick={() => setDomains(current)} disabled={saving}>
                    Reset
                  </Button>
                )}
              </Space>
            </div>
          </div>
        </>
      )}

      {/* Step-up (re-OTP) modal for the domain edit */}
      {modal}
    </Card>
  );
}
